/** Fallback when no media is set in Prismic (`static/audio/` after build). */
const FALLBACK_AUDIO_SRC = "/audio/background.mp3"

let singletonEl = null

/**
 * One shared `<audio>` on `document.body`, outside React. Survives any
 * remount of `wrapPageElement` or page components so playback does not restart.
 *
 * @param {string | null | undefined} srcFromCms Resolved media URL from Prismic (or omit for fallback).
 * @returns {HTMLAudioElement | null}
 */
export function ensureBackgroundAudioElement(srcFromCms) {
  if (typeof document === "undefined") {
    return null
  }

  const resolved =
    typeof srcFromCms === "string" && srcFromCms.trim() ? srcFromCms.trim() : FALLBACK_AUDIO_SRC

  if (singletonEl && document.body.contains(singletonEl)) {
    try {
      const current = new URL(singletonEl.currentSrc, document.baseURI).href
      const next = new URL(resolved, document.baseURI).href
      if (current !== next && singletonEl.paused && singletonEl.currentTime === 0) {
        singletonEl.src = resolved
      }
    } catch {
      if (singletonEl.src !== resolved && singletonEl.paused && singletonEl.currentTime === 0) {
        singletonEl.src = resolved
      }
    }
    return singletonEl
  }
  if (singletonEl && !document.body.contains(singletonEl)) {
    singletonEl.src = resolved
    document.body.appendChild(singletonEl)
    return singletonEl
  }

  const audio = document.createElement("audio")
  audio.src = resolved
  audio.loop = true
  audio.playsInline = true
  audio.preload = "auto"
  audio.setAttribute("aria-hidden", "true")
  audio.className = "sr-only"
  document.body.appendChild(audio)
  singletonEl = audio
  return audio
}
